import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import {
  stockService, MOVEMENT_TYPE_LABEL, type StockItem, type StockMovementType,
} from '@/services/stock.service';

const TYPES: StockMovementType[] = ['in', 'out', 'adjust', 'waste'];

const QUANTITY_LABEL: Record<StockMovementType, string> = {
  in: 'Quantity received',
  out: 'Quantity taken out',
  waste: 'Quantity wasted',
  adjust: 'New total quantity',
};

interface Props {
  open: boolean;
  item: StockItem | null;
  isAdmin: boolean;
  onClose: () => void;
  onRecorded: () => void;
}

export default function RecordMovementModal({ open, item, isAdmin, onClose, onRecorded }: Props) {
  const [type, setType] = useState<StockMovementType>('in');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [note, setNote] = useState('');
  const [allowNegative, setAllowNegative] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType('in'); setQuantity(''); setUnitCost(''); setNote(''); setAllowNegative(false);
  }, [open, item]);

  const save = async () => {
    if (!item) return;
    const qty = Number(quantity);
    if (quantity.trim() === '' || Number.isNaN(qty) || qty < 0) { toast.error('Enter a valid quantity'); return; }
    setBusy(true);
    try {
      await stockService.recordMovement({
        stockUid: item.stockUid,
        type,
        quantity: qty,
        unitCost: unitCost.trim() ? Number(unitCost) : undefined,
        note: note.trim() || undefined,
        allowNegative: (type === 'out' || type === 'waste') ? allowNegative : undefined,
      });
      toast.success('Movement recorded');
      onRecorded();
    } catch (e: any) { if (!e?.response) toast.error('Could not record this movement.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={`Record movement${item ? ` — ${item.name}` : ''}`} widthClass="max-w-sm">
      {item && (
        <p className="mb-3 text-2xs text-text-muted">
          Currently <span className="font-semibold text-text-primary">{item.quantity} {item.unit}</span> in stock.
        </p>
      )}
      <FormField label="Type">
        <Select value={type} onChange={(e) => setType(e.target.value as StockMovementType)}>
          {TYPES.map((t) => <option key={t} value={t}>{MOVEMENT_TYPE_LABEL[t]}</option>)}
        </Select>
      </FormField>
      <FormField label={QUANTITY_LABEL[type]} required>
        <Input type="number" min={0} step="0.001" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" />
      </FormField>
      {(type === 'in' || type === 'waste') && (
        <FormField label="Unit cost" hint="Optional — defaults to current cost">
          <Input type="number" min={0} step="0.0001" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} />
        </FormField>
      )}
      <FormField label="Note" hint="Optional"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Delivery ref, reason…" /></FormField>
      {isAdmin && (type === 'out' || type === 'waste') && (
        <label className="mb-2 flex items-center gap-2 text-xs text-text-secondary">
          <input type="checkbox" checked={allowNegative} onChange={(e) => setAllowNegative(e.target.checked)} className="h-3.5 w-3.5 rounded border-border" />
          Allow this to take stock below zero
        </label>
      )}
      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={busy}>Record</Button>
      </div>
    </Modal>
  );
}
